import type { MeResponse, Passkey } from '@epc/contracts';
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import { ApiError, api } from './api';

/**
 * Вход по Face ID, Touch ID, Windows Hello и отпечатку (ключи входа, passkeys).
 * Биометрия не покидает устройство: оно лишь подписывает одноразовый вызов портала.
 */

const DEVICE_FLAG = 'epc:passkey-on-device';
const OFFER_DISMISSED = 'epc:passkey-offer-dismissed';

export function passkeysSupported(): boolean {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

export async function platformAvailable(): Promise<boolean> {
  if (!passkeysSupported()) return false;
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

/** Название биометрии на этом устройстве — для подписей кнопок */
export function biometryName(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'Face ID';
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return 'Face ID';
  if (/Macintosh/.test(ua)) return 'Touch ID';
  if (/Windows/.test(ua)) return 'Windows Hello';
  if (/Android/.test(ua)) return 'отпечатку';
  return 'биометрии';
}

export function enableLabel(): string {
  const name = biometryName();
  return name === 'отпечатку' || name === 'биометрии' ? `Включить вход по ${name}` : `Включить ${name}`;
}

export function loginLabel(): string {
  const name = biometryName();
  return name === 'Windows Hello' ? 'Войти через Windows Hello' : `Войти по ${name}`;
}

/** На этом устройстве уже включали вход по биометрии — показываем его первым */
export function deviceHasPasskey(): boolean {
  try {
    return localStorage.getItem(DEVICE_FLAG) === '1';
  } catch {
    return false;
  }
}

function markDevice(): void {
  try {
    localStorage.setItem(DEVICE_FLAG, '1');
  } catch {
    /* хранилище браузера недоступно — просто не запоминаем */
  }
}

export function offerDismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(OFFER_DISMISSED) ?? 0);
    return Date.now() - at < 30 * 86_400_000;
  } catch {
    return true;
  }
}

export function dismissOffer(): void {
  try {
    localStorage.setItem(OFFER_DISMISSED, String(Date.now()));
  } catch {
    /* не страшно: предложим ещё раз */
  }
}

/** Пользователь закрыл системное окно Face ID — это не ошибка, сообщение не нужно */
export function isCancel(err: unknown): boolean {
  const e = err as { name?: string; code?: string; cause?: { name?: string } } | null;
  const name = e?.cause?.name ?? e?.name;
  return name === 'NotAllowedError' || name === 'AbortError' || e?.code === 'ERROR_CEREMONY_ABORTED';
}

export function passkeyError(err: unknown): Error | ApiError {
  if (err instanceof ApiError) return err;
  const e = err as { name?: string; code?: string } | null;
  if (e?.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED' || e?.name === 'InvalidStateError') {
    return new Error('На этом устройстве вход по биометрии уже включён.');
  }
  if (
    e?.name === 'SecurityError' ||
    e?.code === 'ERROR_INVALID_DOMAIN' ||
    e?.code === 'ERROR_INVALID_RP_ID'
  ) {
    return new Error('Вход по биометрии работает только на основном адресе портала.');
  }
  return new Error('Устройство не ответило. Попробуйте ещё раз или войдите по паролю.');
}

export async function registerPasskey(): Promise<Passkey> {
  const { options } = await api<{ options: PublicKeyCredentialCreationOptionsJSON }>(
    '/auth/passkeys/register/options',
    { method: 'POST' },
  );
  const response = await startRegistration({ optionsJSON: options });
  const key = await api<Passkey>('/auth/passkeys/register/verify', { body: { response } });
  markDevice();
  return key;
}

async function authenticate(autofill: boolean): Promise<MeResponse> {
  const { challengeId, options } = await api<{
    challengeId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }>('/auth/passkeys/login/options', { method: 'POST' });
  const response = await startAuthentication({ optionsJSON: options, useBrowserAutofill: autofill });
  const me = await api<MeResponse>('/auth/passkeys/login/verify', { body: { challengeId, response } });
  markDevice();
  return me;
}

export function loginWithPasskey(): Promise<MeResponse> {
  return authenticate(false);
}

/**
 * Подсказка ключа прямо в поле почты (iPhone, Safari, Chrome): пользователь касается поля
 * и выбирает «Войти с ключом». Возвращает null, если браузер так не умеет.
 */
export async function autofillPasskey(): Promise<MeResponse | null> {
  try {
    if (!(await browserSupportsWebAuthnAutofill())) return null;
  } catch {
    return null;
  }
  return authenticate(true);
}

export function cancelPasskeyRequest(): void {
  try {
    WebAuthnAbortService.cancelCeremony();
  } catch {
    /* нечего отменять */
  }
}
